import React, { useState, useEffect } from 'react';
import { TargetIcon } from '../icons/ChatCategoryIcons.js';
import { Progress, Spinner, isDeterminateProgress } from '../common/AsyncState';
import { useRegisterLayer } from '../common/LayerContext';

// 토스 스타일 로딩 메시지 (간결하고 명확)
const LOADING_MESSAGES = {
  saenggibu: [
    "학생 정보를 분석하고 있어요",
    "기재요령을 적용하고 있어요", 
    "학생 특성을 파악하고 있어요",
    "전문적인 표현을 작성하고 있어요",
    "적절한 어휘를 선택하고 있어요",
    "문장을 다듬고 있어요",
    "맞춤법과 문법을 검토하고 있어요",
    "품질을 최종 확인하고 있어요"
  ],
  quiz: [
    "문제를 생성하고 있어요",
    "교육과정을 분석하고 있어요",
    "적절한 난이도로 조정하고 있어요"
  ],
  default: [
    "작업을 진행하고 있어요",
    "결과를 생성하고 있어요",
    "잠시만 기다려주세요"
  ]
};

// 생기부 작성 팁 (로딩 중 표시)
const SAENGGIBU_TIPS = [
  { text: "우측 상단 ", link: { text: "'다중 생성'", action: "scrollToModeButtons" }, after: " 기능으로 학급 전체를 한번에 처리할 수 있어요" },
  { text: "위 ", link: { text: "파일 첨부 기능", action: "scrollToFileAttach" }, after: "을 활용하면 정확도가 크게 올라가요" },
  "생성된 내용은 교사 검토 후 사용해주세요",
  "구체적인 활동 내용을 추가하면 더 풍부해져요",
  "좌측 메뉴 '생기부 검사기'로 기재요령 위반을 확인해보세요",
  "고등탭으로 학생 개별 맞춤형 교과 세특을 작성해보세요"
];

// 생기부 검사기 팁 (로딩 중 표시)
const INSPECTOR_TIPS = [
  { text: "우측 상단 ", link: { text: "'다중 검사'", action: "scrollToModeButtons" }, after: " 기능으로 학급 전체를 한번에 검사할 수 있어요" },
  "검사 결과에서 위반사항을 클릭하면 상세 설명을 볼 수 있어요",
  "AI 수정 제안을 참고해서 생기부를 개선해보세요",
  "맞춤법뿐만 아니라 기재요령 원문도 대조해서 검사해요",
  "좌측 메뉴 '생기부 작성'으로 AI 기반 생기부를 생성해보세요"
];

const clampPercent = (value) => Math.max(0, Math.min(100, value));

const TossLoadingSpinner = ({ 
  type = 'default',
  message = null,
  size = 'medium',
  showIcon = true,
  showProgress = false,  // 프로그레스 바 표시 여부
  progressValue = null,  // 실제 진행률(0~100)이 있을 때만 determinate. 없으면 indeterminate 바
  progressLabel = null,
  progressAriaLabel = '작업 진행률',
  showEstimatedTime = true,
  showTips = false,      // 팁 표시 여부
  scrollToModeButtons = null,    // 모드 버튼으로 스크롤하는 함수
  scrollToFileAttach = null,     // 파일 첨부로 스크롤하는 함수
  slowReasons = []
}) => {
  const [currentMessage, setCurrentMessage] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);
  const [currentTip, setCurrentTip] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);
  
  const messages = LOADING_MESSAGES[type] || LOADING_MESSAGES.default;
  const determinateProgress = isDeterminateProgress(progressValue)
    ? clampPercent(progressValue)
    : null;
  
  // 시간 경과 카운터
  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  // 예상 시간 계산 (타입별)
  // inspector: 30초 표시 (단일검사), saenggibu: 60초, 기타: 20초
  const estimatedTime = type === 'inspector' ? 30 : (type === 'saenggibu' ? 60 : 20); // 초 단위
  const timeRemaining = Math.max(0, estimatedTime - elapsedTime);
  const isTakingLong = type === 'saenggibu' && elapsedTime >= 70;
  const elapsedLabel = elapsedTime >= 60
    ? `${Math.floor(elapsedTime / 60)}분 ${elapsedTime % 60}초`
    : `${elapsedTime}초`;
  const visibleSlowReasons = Array.isArray(slowReasons) ? slowReasons.filter(Boolean).slice(0, 2) : [];
  
  // 메시지 순환
  useEffect(() => {
    if (message) {
      setCurrentMessage(message);
      return;
    }
    
    setCurrentMessage(messages[messageIndex]);
    
    // 6초마다 변경
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % messages.length);
    }, 6000);
    
    return () => clearInterval(interval);
  }, [messages, messageIndex, message]);
  
  // 팁 표시 (생기부/검사기 타입일 때만)
  useEffect(() => {
    if (!showTips || (type !== 'saenggibu' && type !== 'inspector')) return;
    
    // 타입에 따른 팁 선택
    const tips = type === 'inspector' ? INSPECTOR_TIPS : SAENGGIBU_TIPS;
    
    // 랜덤 팁 선택
    const randomTip = tips[Math.floor(Math.random() * tips.length)];
    setCurrentTip(randomTip);
    
    const tipInterval = setInterval(() => {
      const randomTip = tips[Math.floor(Math.random() * tips.length)];
      setCurrentTip(randomTip);
    }, 5000); // 5초마다 팁 변경
    
    return () => clearInterval(tipInterval);
  }, [showTips, type]);
  
  // 사이즈별 스타일
  const sizeStyles = {
    small: {
      spinner: 20,
      container: 'var(--whatquiz-space-8)',
      text: 'var(--whatquiz-text-sm)',
      icon: 16
    },
    medium: {
      spinner: 32, 
      container: 'var(--whatquiz-space-12)',
      text: 'var(--whatquiz-text-base)',
      icon: 24
    },
    large: {
      spinner: 48,
      container: 'var(--whatquiz-space-16)', 
      text: 'var(--whatquiz-text-lg)',
      icon: 32
    }
  };
  
  const currentSize = sizeStyles[size] || sizeStyles.medium;
  
  // 팁 클릭 핸들러
  const handleTipClick = (action) => {
    if (action === 'scrollToModeButtons' && scrollToModeButtons) {
      scrollToModeButtons();
    } else if (action === 'scrollToFileAttach' && scrollToFileAttach) {
      scrollToFileAttach();
    }
  };
  
  // 팁 렌더링 함수
  const renderTip = (tip) => {
    if (typeof tip === 'string') {
      return tip;
    }
    
    // 하이퍼링크가 있는 팁
    return (
      <span>
        {tip.text}
        <span
          onClick={() => handleTipClick(tip.link.action)}
          style={{
            color: 'var(--whatquiz-primary, #3182f6)',
            fontWeight: '600',
            cursor: 'pointer',
            textDecoration: 'underline',
            transition: 'color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--whatquiz-primary-hover, #1b64da)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--whatquiz-primary, #3182f6)';
          }}
        >
          {tip.link.text}
        </span>
        {tip.after}
      </span>
    );
  };

  const estimatedTimeText = !showEstimatedTime
    ? null
    : (isTakingLong
      ? `${elapsedLabel}째 작성 중`
      : (timeRemaining > 0
        ? `약 ${timeRemaining >= 60
          ? `${Math.floor(timeRemaining / 60)}분 ${timeRemaining % 60}초`
          : `${timeRemaining}초`} 남음`
        : null));
  
  return (
    <>
      <style>{tossLoadingStyles}</style>
      <div 
        className="toss-loading-container"
        style={{ padding: currentSize.container }}
      >
        {/* 스피너와 아이콘 */}
        <div className="toss-loading-content">
          <div 
            className="toss-spinner-wrapper"
            style={{ 
              width: currentSize.spinner,
              height: currentSize.spinner 
            }}
          >
            <Spinner size={currentSize.spinner} />
            
            {/* 중앙 아이콘 */}
            {showIcon && (
              <div className="toss-spinner-icon">
                {type === 'saenggibu' ? (
                  <TargetIcon 
                    size={currentSize.icon} 
                    color="var(--whatquiz-primary)" 
                  />
                ) : (
                  <div 
                    className="toss-spinner-dot"
                    style={{
                      width: currentSize.icon * 0.3,
                      height: currentSize.icon * 0.3
                    }}
                  />
                )}
              </div>
            )}
          </div>
          
          {/* 메시지 */}
          <div 
            className="toss-loading-message"
            style={{ fontSize: currentSize.text }}
          >
            {currentMessage}
          </div>
          
          {/* 프로그레스 바 — 실제 진행률이 있을 때만 determinate, 가짜 진행률은 만들지 않는다 */}
          {showProgress && (
            <div className="toss-progress-container">
              <Progress
                aria-label={progressAriaLabel}
                label={progressLabel || undefined}
                value={determinateProgress === null ? undefined : determinateProgress}
              />
              {estimatedTimeText && (
                <div className="toss-progress-info">
                  <span className="toss-progress-time">{estimatedTimeText}</span>
                </div>
              )}
            </div>
          )}

          {isTakingLong && (
            <div className="toss-loading-slow-note">
              <strong>조금 더 걸리고 있어요</strong>
              <span>
                {visibleSlowReasons.length > 0
                  ? visibleSlowReasons.join(' · ')
                  : '내용을 길게 다듬는 중이에요'}
              </span>
              <small>창을 닫지 않으면 결과가 이어서 표시돼요.</small>
            </div>
          )}
          
          {/* 팁 표시 */}
          {showTips && currentTip && (
            <div className="toss-loading-tip">
              <span className="toss-tip-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--whatquiz-primary, #3182f6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18h6"/><path d="M10 22h4"/>
                  <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"/>
                </svg>
              </span>
              <span className="toss-tip-text">{renderTip(currentTip)}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// 토스 스타일 CSS
const tossLoadingStyles = `
  /* 메인 컨테이너 */
  .toss-loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
  }
  
  .toss-loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--whatquiz-space-4);
  }
  
  /* 스피너 래퍼 — 스피너(AsyncState)는 래퍼를 꽉 채우고, 아이콘은 그 중앙에 겹친다 */
  .toss-spinner-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .toss-spinner-wrapper .whatquiz-spinner {
    position: absolute;
    inset: 0;
  }
  
  /* 중앙 아이콘 */
  .toss-spinner-icon {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  /* 기본 도트 */
  .toss-spinner-dot {
    background: var(--whatquiz-primary);
    border-radius: var(--whatquiz-radius-full);
  }
  
  /* 로딩 메시지 — 한 번만 부드럽게 나타난다 (무한 반복 모션 없음) */
  .toss-loading-message {
    font-weight: var(--whatquiz-font-medium);
    color: var(--whatquiz-gray-700);
    text-align: center;
    line-height: var(--whatquiz-leading-relaxed);
    animation: tossFadeIn 180ms ease-out;
  }
  
  @keyframes tossFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  /* 프로그레스 바 — 막대 자체는 AsyncState의 Progress가 그린다 */
  .toss-progress-container {
    width: 100%;
    max-width: 300px;
    margin-top: var(--whatquiz-space-4);
  }
  
  .toss-progress-info {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: var(--whatquiz-space-2);
    font-size: var(--whatquiz-text-sm);
    color: var(--whatquiz-gray-600);
  }

  .toss-loading-slow-note {
    width: 100%;
    max-width: 360px;
    margin-top: var(--whatquiz-space-2);
    padding: 14px 16px;
    border-radius: 16px;
    background: #f8f9fa;
    color: var(--whatquiz-gray-700);
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .toss-loading-slow-note strong {
    color: var(--whatquiz-gray-900);
    font-size: 14px;
    font-weight: 700;
  }

  .toss-loading-slow-note span {
    font-size: 13px;
    line-height: 1.5;
  }

  .toss-loading-slow-note small {
    color: var(--whatquiz-gray-500);
    font-size: 12px;
  }
  
  .toss-progress-time {
    font-size: 12px;
    color: var(--whatquiz-gray-500);
  }
  
  /* 팁 표시 */
  .toss-loading-tip {
    display: flex;
    align-items: center;
    gap: var(--whatquiz-space-2);
    margin-top: var(--whatquiz-space-6);
    padding: var(--whatquiz-space-3) var(--whatquiz-space-4);
    background: #f3f4f6;
    border: none;
    border-radius: var(--whatquiz-radius-lg);
    max-width: 400px;
    animation: tossFadeIn 180ms ease-out;
  }
  
  .toss-tip-icon {
    font-size: 18px;
    flex-shrink: 0;
  }
  
  .toss-tip-text {
    font-size: 13px;
    line-height: 1.5;
    color: var(--whatquiz-gray-700);
    text-align: left;
  }
  
  /* 풀스크린 오버레이 버전 */
  .toss-loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .toss-loading-overlay .toss-loading-container {
    min-height: auto;
    padding: var(--whatquiz-space-12);
    background: var(--whatquiz-white, #fff);
    border-radius: 16px;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  }

  @media (prefers-reduced-motion: reduce) {
    .toss-loading-message,
    .toss-loading-tip {
      animation: none;
    }
  }
`;

// 풀스크린 로딩 오버레이 컴포넌트
// - 배경을 눌러도 닫히지 않는다 (진행 중 작업을 실수로 끊지 않도록). onClose 같은 추가 prop은 조용히 무시한다.
// - 열려 있는 동안 레이어로 등록되어 토스트가 기다린다.
export const TossLoadingOverlay = ({ 
  isVisible, 
  type = 'default',
  message
}) => {
  useRegisterLayer(Boolean(isVisible));

  if (!isVisible) return null;
  
  return (
    <>
      <style>{tossLoadingStyles}</style>
      <div
        aria-label={message || '처리하고 있어요'}
        aria-modal="true"
        className="toss-loading-overlay"
        role="dialog"
      >
        <TossLoadingSpinner 
          type={type}
          message={message}
          size="large"
          showIcon={true}
        />
      </div>
    </>
  );
};

export default TossLoadingSpinner;
